import type { Locale } from '@shared/locales';
import permissions from '../../../contract/permissions.json';
import { permissionLabels, scopeLabels, scopeWords } from './permission-labels';

/**
 * Markdown generated from contract/permissions.json at build time and spliced
 * into the documentation where an article says `<!-- roles-table -->` (and the
 * other placeholders below). The docs therefore cannot drift from the matrix
 * the platform enforces: change the contract, rebuild, and the tables follow.
 */

interface PermissionEntry {
  roles: string[];
  scope: string;
  fresh_auth?: boolean;
  status?: string;
  notes?: string;
}

const roles = permissions.roles as Record<
  string,
  { rank: number; label: Record<Locale, string>; description: Record<Locale, string> }
>;
const roleOrder = Object.keys(roles).sort((a, b) => roles[b]!.rank - roles[a]!.rank);
const entries = Object.entries(permissions.permissions as Record<string, PermissionEntry>);

const words = {
  en: {
    permission: 'Permission',
    scope: 'Applies to',
    yes: 'Yes',
    no: 'No',
    fresh: 'fresh sign-in',
    scopeHeader: 'Scope',
    grants: 'Includes',
  },
  es: {
    permission: 'Permiso',
    scope: 'Se aplica a',
    yes: 'Sí',
    no: 'No',
    fresh: 'nueva verificación',
    scopeHeader: 'Alcance',
    grants: 'Incluye',
  },
} as const;

const label = (key: string, locale: Locale) => permissionLabels[key]?.[locale] ?? key;
const cell = (value: string) => value.replaceAll('|', '\\|');

export function rolesSummaryMarkdown(locale: Locale): string {
  return roleOrder.map((role) => `- **${roles[role]!.label[locale]}**: ${roles[role]!.description[locale]}`).join('\n');
}

export function rolesTableMarkdown(locale: Locale): string {
  const w = words[locale];
  const header = `| ${w.permission} | ${w.scope} | ${roleOrder.map((role) => roles[role]!.label[locale]).join(' | ')} |`;
  const divider = `| --- | --- | ${roleOrder.map(() => ':---:').join(' | ')} |`;
  const rows = entries
    .filter(([, entry]) => entry.status !== 'reserved')
    .map(([key, entry]) => {
      const name = `${label(key, locale)}${entry.fresh_auth ? ` (${w.fresh})` : ''}`;
      const scope = scopeWords[entry.scope]?.[locale] ?? entry.scope;
      const marks = roleOrder.map((role) => (entry.roles.includes(role) ? `✓ ${w.yes}` : `— ${w.no}`));
      return `| ${cell(name)} | ${scope} | ${marks.join(' | ')} |`;
    });
  return [header, divider, ...rows].join('\n');
}

export function reservedPermissionsMarkdown(locale: Locale): string {
  return entries
    .filter(([, entry]) => entry.status === 'reserved')
    .map(([key]) => `- ${label(key, locale)}`)
    .join('\n');
}

export function apiScopesTableMarkdown(locale: Locale): string {
  const w = words[locale];
  const scopes = permissions.apiScopes.scopes as Record<string, string[]>;
  return [
    `| ${w.scopeHeader} | ${w.grants} |`,
    '| --- | --- |',
    ...Object.entries(scopes).map(
      ([scope, granted]) =>
        `| \`${scope}\` — ${cell(scopeLabels[scope]?.[locale] ?? scope)} | ${granted.map((key) => label(key, locale)).join(', ')} |`,
    ),
  ].join('\n');
}

const PLACEHOLDERS: Record<string, (locale: Locale) => string> = {
  'roles-summary': rolesSummaryMarkdown,
  'roles-table': rolesTableMarkdown,
  'reserved-permissions': reservedPermissionsMarkdown,
  'api-scopes-table': apiScopesTableMarkdown,
};

/** Replaces `<!-- roles-table -->` and the other generated-content placeholders. */
export function expandGeneratedContent(markdown: string, locale: Locale): string {
  return markdown.replace(/<!--\s*([a-z-]+)\s*-->/g, (match, name: string) => {
    const generate = PLACEHOLDERS[name];
    return generate ? generate(locale) : match;
  });
}

export const GENERATED_PLACEHOLDERS = Object.keys(PLACEHOLDERS);
export { permissions as permissionsContract };
